if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.verticalThumbnailsMql = window.matchMedia('(min-width: 990px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.initVerticalThumbnailControls();
      }

      initVerticalThumbnailControls() {
        if (this.dataset.desktopLayout !== 'thumbnail_slider') return;

        this.verticalThumbnailList = this.elements.thumbnails.slider;
        this.verticalThumbnailPreviousButton = this.elements.thumbnails.querySelector('button[name="previous"]');
        this.verticalThumbnailNextButton = this.elements.thumbnails.querySelector('button[name="next"]');

        if (
          !this.verticalThumbnailList ||
          !this.verticalThumbnailPreviousButton ||
          !this.verticalThumbnailNextButton
        ) {
          return;
        }

        this.verticalThumbnailPreviousButton.addEventListener(
          'click',
          this.onVerticalThumbnailButtonClick.bind(this),
          true
        );
        this.verticalThumbnailNextButton.addEventListener(
          'click',
          this.onVerticalThumbnailButtonClick.bind(this),
          true
        );
        this.verticalThumbnailList.addEventListener('scroll', this.updateVerticalThumbnailButtons.bind(this), {
          passive: true,
        });
        this.verticalThumbnailsMql.addEventListener('change', this.updateVerticalThumbnailControls.bind(this));

        this.verticalThumbnailResizeObserver = new ResizeObserver(() => this.updateVerticalThumbnailControls());
        this.verticalThumbnailResizeObserver.observe(this.elements.viewer);
        this.verticalThumbnailResizeObserver.observe(this.verticalThumbnailList);

        this.verticalThumbnailList.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', this.updateVerticalThumbnailControls.bind(this), { once: true });
        });

        requestAnimationFrame(() => this.updateVerticalThumbnailControls());
      }

      updateVerticalThumbnailControls() {
        if (!this.verticalThumbnailList) return;

        if (!this.verticalThumbnailsMql.matches) {
          this.elements.thumbnails.classList.remove('has-overflowing-thumbnails');
          return;
        }

        const hasOverflow = this.verticalThumbnailList.scrollHeight > this.elements.thumbnails.clientHeight + 1;
        this.elements.thumbnails.classList.toggle('has-overflowing-thumbnails', hasOverflow);

        requestAnimationFrame(() => this.updateVerticalThumbnailButtons());
      }

      updateVerticalThumbnailButtons() {
        if (!this.verticalThumbnailList || !this.verticalThumbnailsMql.matches) return;

        const maxScrollTop = this.verticalThumbnailList.scrollHeight - this.verticalThumbnailList.clientHeight;
        this.verticalThumbnailPreviousButton.toggleAttribute('disabled', this.verticalThumbnailList.scrollTop <= 1);
        this.verticalThumbnailNextButton.toggleAttribute(
          'disabled',
          this.verticalThumbnailList.scrollTop >= maxScrollTop - 1
        );
      }

      onVerticalThumbnailButtonClick(event) {
        if (!this.verticalThumbnailsMql.matches) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        this.verticalThumbnailList.scrollBy({
          top: direction * Math.max(this.verticalThumbnailList.clientHeight - 20, 80),
          behavior: 'smooth',
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.dataset.desktopLayout === 'thumbnail_slider' && this.verticalThumbnailsMql.matches) {
          const thumbnailList = this.elements.thumbnails.slider;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = thumbnailList.scrollTop;
          const visibleBottom = visibleTop + thumbnailList.clientHeight;

          if (thumbnailTop < visibleTop) {
            thumbnailList.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          } else if (thumbnailBottom > visibleBottom) {
            thumbnailList.scrollTo({ top: thumbnailBottom - thumbnailList.clientHeight, behavior: 'smooth' });
          }
          requestAnimationFrame(() => this.updateVerticalThumbnailButtons());
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
